/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';

import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';

/**
 * The two smallest repeated shapes of the deal / sale / lease detail screens.
 *
 * These lived three times over before this file existed: as `SurfaceCard` and
 * `MicroLabel` in the sales feature, and as the `DEAL_PANEL_CLASS` string plus
 * `DealPanelLabel` in the deals feature, with the leases feature hand-rolling
 * the same border/radius/background triple in four more places. They had
 * already drifted — the leases copies carry a shadow the others do not.
 *
 * Colours come through tokens rather than a colour utility so a class-merge
 * race cannot drop them.
 */

export interface SurfaceCardProps {
  children: ReactNode;
  /** Extra classes for layout or spacing. Overrides the default padding. */
  className?: string;
  testId?: string;
}

/**
 * A bordered surface card — the single container shape of the detail screens.
 *
 * A hairline border lifting the card off the warm page ground, no shadow. The
 * default padding is `--space-4`; the detail-screen strips and timelines pass
 * their own roomier `px-(--space-5) py-(--space-4)`.
 *
 * @param props.children - The card contents.
 * @param props.className - Extra classes for layout or spacing.
 * @param props.testId - Stable selector for tests.
 * @returns The rendered card.
 */
export function SurfaceCard({ children, className, testId }: SurfaceCardProps) {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col rounded-(--radius-lg) border border-(--border-subtle)',
        'bg-(--surface-card) p-(--space-4)',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface MicroLabelProps {
  children: ReactNode;
  className?: string;
}

/**
 * The small caption that labels a figure or a column.
 *
 * Sentence case: it used to be all-caps with the wider tracking capitals
 * need, which read as shouting a label the eye was meant to skim past on
 * the way to the figure under it.
 *
 * @param props.children - The caption text.
 * @param props.className - Extra classes for layout.
 * @returns The rendered micro-label.
 */
export function MicroLabel({ children, className }: MicroLabelProps) {
  return (
    <Text
      component="span"
      className={cn(
        'text-[length:var(--text-meta)] leading-[var(--leading-snug)]',
        'text-(--text-tertiary-soft)',
        className,
      )}
    >
      {children}
    </Text>
  );
}
